import * as React from 'react';

import { Button } from '../../atoms/Button';
import { CAP_TRIM, cn } from '../../lib/utils';

export interface WizardPanelProps {
  /** Step heading (rendered as h1). Omit when the step supplies its own header. */
  title?: string;
  /** Appends a muted "· optional" tag after the title (IdP / HR steps). */
  optional?: boolean;
  /** Lead paragraph under the title. `<b>` is styled darker via `[&_b]`. */
  subtitle?: React.ReactNode;
  /** Step body — sections composed by each step. */
  children?: React.ReactNode;

  /** Back button (bottom-left). Omit to right-align the footer. */
  onBack?: () => void;
  backLabel?: string;
  /** `data-testid` for the Back button. Overridable so the shell stays app-agnostic. */
  backTestId?: string;
  /** Optional secondary "Skip" action, grouped left of the primary CTA. */
  onSkip?: () => void;
  skipLabel?: string;
  /** `data-testid` for the Skip button. */
  skipTestId?: string;
  /** Primary CTA (bottom-right). */
  onContinue?: () => void;
  continueLabel?: string;
  /** Renders the CTA in its disabled state (steps with an unmet gate). */
  continueDisabled?: boolean;
  /** `data-testid` for the Continue button. Overridable so the shell stays app-agnostic. */
  continueTestId?: string;

  /**
   * Drop the standalone card chrome (fixed width, border, radius, shadow,
   * padding) so the panel can fill a container that draws the card itself
   * (e.g. an `OnboardingCard`, which already provides `p-10`).
   */
  frameless?: boolean;

  /**
   * Typography + footer spec. `default` is the original wizard shell (24px bold
   * title, grey-500 lead, divider above the footer, hug-width CTA); `v2` is the
   * onboarding refresh (Figma `2776:8592`): a 20px medium title, a grey-400
   * lead capped at 480px, 40px between header and body, and a divider-less
   * footer with a fixed 180px CTA. Existing steps stay on `default`.
   */
  variant?: 'default' | 'v2';

  className?: string;
}

/**
 * WizardPanel — the white content panel of the onboarding wizard (the right
 * side of `onboard-wizard.html`; the left progress rail is out of scope per the
 * ticket). Shared shell for every step: title + optional tag, subtitle, a body
 * slot, and a footer that lays out Back / Skip / Continue exactly like the mock
 * (Back left, the rest right; right-aligned when there is no Back). All colours,
 * radii and type come from Dawn tokens — no raw hex.
 */
export const WizardPanel = React.forwardRef<HTMLDivElement, WizardPanelProps>(
  (
    {
      title,
      optional,
      subtitle,
      children,
      onBack,
      backLabel = 'Back',
      backTestId = 'wizard-back',
      onSkip,
      skipLabel = 'Skip',
      skipTestId,
      onContinue,
      continueLabel = 'Continue',
      continueDisabled,
      continueTestId = 'wizard-next',
      frameless,
      variant = 'default',
      className,
    },
    ref
  ) => {
    const v2 = variant === 'v2';
    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col',
          frameless
            ? 'h-full min-h-0 w-full flex-1'
            : cn(
                'w-[600px] min-h-[560px] rounded-xl border border-grey-200 bg-dawn-white p-10',
                'shadow-[0_1px_2px_rgba(27,27,27,0.04),0_8px_28px_rgba(27,27,27,0.06)]'
              ),
          className
        )}
      >
        {/* Everything except the footer scrolls; the footer stays pinned so the
            Continue button keeps its fixed 40px offset from the card's bottom
            edge (the card's own p-10) however tall the step body grows. */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {title && (
            <h1
              className={cn(
                'text-dawn-black',
                // Figma `2776:8599`: 20 Medium, cap-trimmed like every text box
                // in the design — untrimmed line boxes inflate the 20/40px gaps.
                v2 ? cn('text-[20px] font-medium leading-[1.2]', CAP_TRIM) : 'text-h1'
              )}
            >
              {title}
              {optional && (
                <span className="ml-2 align-middle text-p2 font-medium uppercase tracking-[0.3px] text-grey-400">
                  · optional
                </span>
              )}
            </h1>
          )}

          {subtitle && (
            <p
              className={cn(
                // text-[13px], not text-p1: tailwind-merge misreads the custom
                // `text-p1` token as a colour and drops it against the
                // text-grey-* below, leaving the browser's 16px default.
                'text-[13px] font-normal [&_b]:font-semibold [&_b]:text-dawn-black',
                v2
                  ? cn('mt-5 max-w-[480px] leading-[1.3] text-grey-400', CAP_TRIM)
                  : 'mt-2 max-w-[440px] leading-[1.55] text-grey-500'
              )}
            >
              {subtitle}
            </p>
          )}

          <div className={cn('flex-1', v2 && 'mt-10')}>{children}</div>
        </div>

        {(onBack || onSkip || onContinue) && (
          <div
            className={cn(
              'mt-auto flex items-center',
              v2 ? 'pt-10' : 'border-t border-grey-200 pt-5',
              onBack ? 'justify-between' : 'justify-end'
            )}
          >
            {onBack && (
              <Button variant="outline" data-testid={backTestId} onClick={onBack}>
                {backLabel}
              </Button>
            )}
            <div className="flex gap-2.5">
              {onSkip && (
                <Button variant="outline" data-testid={skipTestId} onClick={onSkip}>
                  {skipLabel}
                </Button>
              )}
              {onContinue && (
                <Button
                  data-testid={continueTestId}
                  onClick={onContinue}
                  disabled={continueDisabled}
                  className={cn(v2 && 'w-[180px]')}
                >
                  {continueLabel}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

WizardPanel.displayName = 'WizardPanel';

/** Uppercase section eyebrow used above each block in a wizard step. */
export function WizardSectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.7px] text-grey-400">
      {children}
    </div>
  );
}
